// src/pages/Enseignants/Attendance/AttendancePage.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUserCheck } from "react-icons/fa";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import useScrollToTop from "../../../hooks/useScollToTop";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

import { useAttendanceMobile } from "./hooks/useAttendanceMobile";

import AttendanceMobileHero from "./components/AttendanceMobileHero";
import AttendanceGroupPickerPanel from "./components/AttendanceGroupPickerPanel";
import AttendanceFiltersPanel from "./components/AttendanceFiltersPanel";
import AttendanceKpiCards from "./components/AttendanceKpiCards";
import AttendanceAdherentsGrid from "./components/AttendanceAdherentsGrid";
import AttendanceEmptyState from "./components/AttendanceEmptyState";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getRoleId(user, storedUser) {
  return Number(
    user?.role_id ||
      user?.roleId ||
      storedUser?.role_id ||
      storedUser?.roleId ||
      0
  );
}

function UnauthorizedPanel() {
  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Présence des adhérents
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs et enseignants.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AttendancePage() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user, storedUser);

  const userRole = getRoleFromRoleId(roleId);
  const authorized = roleId === 1 || roleId === 3;
  const isAdmin = roleId === 1;

  const {
    adherents,
    filteredAdherents,
    stats,

    filters,
    setFilters,

    seasons,
    dojos,
    groups,

    attendance,
    assiduityInfo,
    birthdaySet,

    loading,
    savingIds,

    refresh,
    handleToggleAttendance,
    handleDeleteAllAttendances,
    loadAssiduity,
  } = useAttendanceMobile();

  const groupSelected = Boolean(filters.filterGroup);

  if (!authorized) {
    return <UnauthorizedPanel />;
  }

  return (
    <section className="w-full pb-10 font-satoshi">
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl space-y-5 px-3 sm:px-6">
        <HeaderTitle
          role="Enseignant"
          roleId="3"
          title="Gestion des appels"
          subtitle="Interface mobile pour faire l’appel rapidement depuis le tatami."
          icon={FaUserCheck}
          align="left"
          className="mb-2"
        />

        <div className="mb-4">
          <DashboardNavigation role={userRole} />
        </div>

        <AttendanceMobileHero loading={loading} onRefresh={refresh} />

        {!groupSelected ? (
          <AttendanceGroupPickerPanel
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            seasons={seasons}
            dojos={dojos}
            groups={groups}
          />
        ) : (
          <>
            <AttendanceFiltersPanel
              loading={loading}
              filters={filters}
              setFilters={setFilters}
              seasons={seasons}
              dojos={dojos}
              isAdmin={isAdmin}
              onRefresh={refresh}
              onDeleteAll={handleDeleteAllAttendances}
            />

            <AttendanceKpiCards stats={stats} loading={loading} />

            <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

              <div className="p-4 sm:p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                      Appel du cours
                    </p>

                    <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                      Groupe {filters.filterGroup}
                    </h2>

                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      Touchez une carte : non saisi → présent → absent → non
                      saisi.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                    <span className="text-slate-500 dark:text-slate-400">
                      Affichés :
                    </span>{" "}
                    <span className="font-bold text-slate-900 dark:text-white">
                      {filteredAdherents.length}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {" "}
                      / {adherents.length}
                    </span>
                  </div>
                </div>

                {loading ? (
                  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {[...Array(8)].map((_, index) => (
                      <div
                        key={index}
                        className="h-[230px] animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                      />
                    ))}
                  </div>
                ) : filteredAdherents.length === 0 ? (
                  <AttendanceEmptyState />
                ) : (
                  <div className="mt-5">
                    <AttendanceAdherentsGrid
                      adherents={filteredAdherents}
                      attendance={attendance}
                      savingIds={savingIds}
                      assiduityInfo={assiduityInfo}
                      birthdaySet={birthdaySet}
                      onToggle={handleToggleAttendance}
                      requestAssiduity={loadAssiduity}
                    />
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      <ToastContainer />
    </section>
  );
}